/*
  elva-cart-instant.js — cart drawer behaviour fixes for Elva.
  Part 1: open the drawer the moment "Add to cart" is clicked.
  Part 2: let a shopper actually remove the Package Protection line (see bottom).

  What part 1 fixes:
  Horizon's cart-drawer.js only auto-opens the drawer when BOTH
    (a) `settings.auto_open_cart_drawer` is on — it is off on this store, so the
        drawer never opened after an add at all, and
    (b) the drawer is not currently rendering its empty state:
          shouldAutoOpen && !sourceModal && !this.#isCartEmpty() && themeDrawer.open()
        On a shopper's FIRST add the drawer still shows the empty state, so even with
        (a) on, the open waits for POST /cart/add.js and then GET /cart.js — two
        sequential round trips, measured at ~854ms on this store.

  This listens for the same `shopify:cart:lines-update` event Horizon dispatches
  synchronously on click (measured at ~30ms), opens the drawer right away, and covers
  the empty state with a skeleton until the real cart content morphs in.
*/
(function () {
  var EVENT = 'shopify:cart:lines-update';
  var LOADING_CLASS = 'elva-cart-loading';
  var SKELETON_CLASS = 'elva-cart-skel';
  var SAFETY_MS = 12000;

  var guard = null;

  function drawer() {
    return document.querySelector('theme-drawer#cart-drawer');
  }

  function inner() {
    return document.querySelector('cart-drawer-component .cart-drawer__inner');
  }

  function injectStyles() {
    if (document.getElementById('elva-cart-instant-styles')) return;
    var s = document.createElement('style');
    s.id = 'elva-cart-instant-styles';
    s.textContent = [
      '@keyframes elvaCartShimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}',
      /* while loading, the drawer shows only the skeleton — not "your cart is empty" */
      '.cart-drawer__inner.' + LOADING_CLASS + ' > *:not(.' + SKELETON_CLASS + '){display:none}',
      '.' + SKELETON_CLASS + '{display:flex;flex-direction:column;height:100%;width:100%;',
      'padding:var(--cart-drawer-padding,1.5rem);gap:1.25rem;box-sizing:border-box}',
      '.' + SKELETON_CLASS + ' i{display:block;border-radius:.5rem;background:#ECECEC;',
      'background-image:linear-gradient(90deg,#ECECEC 0%,#F7F7F7 50%,#ECECEC 100%);',
      'background-size:200% 100%;animation:elvaCartShimmer 1.1s linear infinite}',
      '.' + SKELETON_CLASS + '__head{display:flex;justify-content:space-between;align-items:center}',
      '.' + SKELETON_CLASS + '__row{display:flex;gap:1rem;align-items:flex-start;padding-bottom:1.25rem;',
      'border-bottom:1px solid rgba(0,0,0,.08)}',
      /* direct child only: the text bars are also :first-child inside __lines */
      '.' + SKELETON_CLASS + '__row > i:first-child{flex:0 0 4.5rem;height:4.5rem;border-radius:.5rem}',
      '.' + SKELETON_CLASS + '__lines{flex:1 1 auto;display:flex;flex-direction:column;gap:.6rem;padding-top:.35rem}',
      '.' + SKELETON_CLASS + '__foot{margin-top:auto;display:flex;flex-direction:column;gap:1rem}',
      '@media (prefers-reduced-motion:reduce){.' + SKELETON_CLASS + ' i{animation:none}}',
    ].join('');
    document.head.appendChild(s);
  }

  function buildSkeleton(rows) {
    var el = document.createElement('div');
    el.className = SKELETON_CLASS;
    el.setAttribute('aria-hidden', 'true');
    var html =
      '<div class="' + SKELETON_CLASS + '__head"><i style="width:35%;height:1.4rem"></i>' +
      '<i style="width:1.5rem;height:1.5rem;border-radius:50%"></i></div>';
    for (var i = 0; i < rows; i++) {
      html +=
        '<div class="' + SKELETON_CLASS + '__row"><i></i>' +
        '<div class="' + SKELETON_CLASS + '__lines">' +
        '<i style="width:70%;height:.9rem"></i>' +
        '<i style="width:40%;height:.9rem"></i>' +
        '<i style="width:25%;height:.9rem"></i>' +
        '</div></div>';
    }
    html +=
      '<div class="' + SKELETON_CLASS + '__foot">' +
      '<i style="width:55%;height:1.1rem"></i>' +
      '<i style="width:100%;height:3rem;border-radius:.85rem"></i>' +
      '</div>';
    el.innerHTML = html;
    return el;
  }

  function clearSkeleton() {
    if (guard) {
      clearTimeout(guard);
      guard = null;
    }
    var box = inner();
    if (box) box.classList.remove(LOADING_CLASS);
    var nodes = document.querySelectorAll('.' + SKELETON_CLASS);
    for (var i = 0; i < nodes.length; i++) nodes[i].remove();
  }

  function showSkeleton() {
    var box = inner();
    if (!box || box.querySelector('.' + SKELETON_CLASS)) return;
    injectStyles();
    /* One row per line already in the cart, plus the one being added. */
    var existing = box.querySelectorAll('.cart-items__table-row').length;
    box.appendChild(buildSkeleton(Math.min(existing + 1, 3)));
    box.classList.add(LOADING_CLASS);
    guard = setTimeout(clearSkeleton, SAFETY_MS);
  }

  document.addEventListener(
    EVENT,
    function (event) {
      if (event.action !== 'add') return;
      var d = drawer();
      if (!d || typeof d.open !== 'function') return;

      /* Adds made from inside another modal keep Horizon's own behaviour. */
      var sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
      if (sourceModal) return;

      /* Only cover content that is about to be replaced anyway. A drawer that
         already lists items is a better placeholder than any skeleton. */
      var box = inner();
      if (box && box.classList.contains('cart-drawer--empty')) showSkeleton();

      if (!d.isOpen) d.open();

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(clearSkeleton, clearSkeleton);
      } else {
        clearSkeleton();
      }
    },
    true
  );

  /* Horizon morphs the drawer content in after the cart refresh; once real line
     items exist the skeleton has nothing left to hide. */
  document.addEventListener('DOMContentLoaded', function () {
    var box = inner();
    if (!box) return;
    new MutationObserver(function () {
      var live = inner();
      if (live && live.classList.contains(LOADING_CLASS) && live.querySelector('.cart-items__table-row')) {
        clearSkeleton();
      }
    }).observe(box.parentNode || box, { childList: true, subtree: true });
  });

  /* ------------------------------------------------------------------------
     Part 2 — removing the Package Protection line has to stick.

     cart-protection.liquid renders the toggle with data-auto="true" and derives
     its checked state from the cart, and elva-cart-extras.js re-adds protection
     800ms after any cart update unless localStorage['elva-protect-off'] is set.
     That flag is written ONLY by the toggle's own change handler — so removing
     the line from the item list leaves the toggle rendered unchecked with no
     flag, and maybeAutoProtect() silently puts the product straight back.

     Treat an explicit row removal as the same opt-out the toggle performs.
     ------------------------------------------------------------------------ */
  var PROTECT_OFF_KEY = 'elva-protect-off';
  var PROTECTION_HANDLE = '/products/package-protection';

  function protectionVariantId() {
    var toggle = document.querySelector('[data-elva-protect-toggle][data-variant-id]');
    return toggle ? toggle.getAttribute('data-variant-id') : null;
  }

  function isProtectionRow(row) {
    if (!row) return false;
    /* Line keys are "<variantId>:<hash>". */
    var vid = protectionVariantId();
    var key = row.dataset ? row.dataset.key : null;
    if (vid && key && key.split(':')[0] === vid) return true;
    /* Fallback for any other key format: the row links to the product page. */
    var link = row.querySelector('a[href*="/products/"]');
    return !!link && link.getAttribute('href').indexOf(PROTECTION_HANDLE) !== -1;
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('[ref="removeButton"], button[aria-label^="Remove" i]');
      if (!btn) return;
      if (!isProtectionRow(btn.closest('.cart-items__table-row'))) return;
      try {
        localStorage.setItem(PROTECT_OFF_KEY, '1');
      } catch (err) {
        /* private mode: nothing we can do, the re-add stays as it was */
      }
    },
    true
  );
})();
